/**
 * Digit and letter normalization for Persian / Arabic input.
 *
 * `normalizeCode` implements docs/contracts/whmcs-bridge.md §6 and MUST stay
 * byte-for-byte compatible with the PHP bridge (`mzaff`). See the notes on
 * trimming and uppercasing below.
 */

const PERSIAN_ZERO = 0x06f0; // ۰
const ARABIC_INDIC_ZERO = 0x0660; // ٠

/** Convert Persian (۰-۹) and Arabic-Indic (٠-٩) digits to ASCII 0-9. Other characters are untouched. */
export function toAsciiDigits(input: string): string {
  return input.replace(/[۰-۹٠-٩]/g, (ch) => {
    const cp = ch.charCodeAt(0);
    const base = cp >= PERSIAN_ZERO ? PERSIAN_ZERO : ARABIC_INDIC_ZERO;
    return String.fromCharCode(48 + cp - base);
  });
}

/** Convert ASCII and Arabic-Indic digits to Persian digits (۰-۹). Other characters are untouched. */
export function toPersianDigits(input: string | number | bigint): string {
  return String(input).replace(/[0-9٠-٩]/g, (ch) => {
    const cp = ch.charCodeAt(0);
    const offset = cp <= 57 ? cp - 48 : cp - ARABIC_INDIC_ZERO;
    return String.fromCharCode(PERSIAN_ZERO + offset);
  });
}

/**
 * Replace Arabic letter forms with their Persian equivalents:
 * `ي` (U+064A) and `ى` (U+0649) become `ی` (U+06CC), `ك` (U+0643) becomes `ک` (U+06A9).
 */
export function normalizeArabicLetters(input: string): string {
  return input.replace(/[يى]/g, 'ی').replace(/ك/g, 'ک');
}

/** Characters stripped by PHP's default `trim()`, so both sides of the bridge agree. */
const PHP_TRIM_RE = /^[ \t\n\r\0\x0B]+|[ \t\n\r\0\x0B]+$/g;

/** Valid referral / promo code after normalization (contract §6 step 4). */
export const CODE_PATTERN = /^[A-Z0-9]{3,16}$/;

/**
 * Normalize a referral or promo code exactly per contract §6:
 * 1. trim, Persian / Arabic-Indic digits to ASCII
 * 2. Arabic `ي`/`ك` to Persian `ی`/`ک`
 * 3. uppercase
 * 4. validate against `^[A-Z0-9]{3,16}$`
 *
 * Returns the normalized code, or `null` when it is not a valid code.
 *
 * Parity notes for the PHP side: trimming uses PHP's default `trim()` set
 * (space, \t, \n, \r, \0, \x0B), and uppercasing is ASCII-only like PHP 8's
 * `strtoupper()`. JavaScript's `toUpperCase()` would map e.g. `ı` to `I`,
 * which PHP does not, so it is deliberately not used.
 */
export function normalizeCode(input: string | null | undefined): string | null {
  if (input == null) return null;
  let s = input.replace(PHP_TRIM_RE, '');
  s = toAsciiDigits(s);
  s = normalizeArabicLetters(s);
  s = s.replace(/[a-z]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 32));
  return CODE_PATTERN.test(s) ? s : null;
}

/** A generated discount code: the partner's prefix, a hyphen and digits, e.g. `SHETABWP-48213`. */
const PROMO_SERIAL_PATTERN = /^[A-Z0-9]{2,10}-[0-9]{4,6}$/;

/**
 * Normalize a discount code typed at checkout: everything `normalizeCode` accepts, plus the
 * hyphenated codes the platform generates (`PREFIX-12345`). Referral codes never take a hyphen.
 * Same steps as `normalizeCode`, and the same in the WHMCS bridge (Codes::normalizePromoOrNull).
 */
export function normalizePromoCode(input: string | null | undefined): string | null {
  if (input == null) return null;
  let s = input.replace(PHP_TRIM_RE, '');
  s = toAsciiDigits(s);
  s = normalizeArabicLetters(s);
  s = s.replace(/[a-z]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 32));
  return CODE_PATTERN.test(s) || PROMO_SERIAL_PATTERN.test(s) ? s : null;
}
